import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast, { Toaster } from "react-hot-toast";

import { Container, Row, Col, Card, CardHeader, Button } from "@/Components/ui";
import { membershipService } from "../../api/services/membership.service";
import { MemberInvitationConfirmResponse, MemberInvitationUser } from "../../api/types/member";

interface InviteRegisterRouteState {
  email?: string;
  site_id?: number;
  permission?: number;
  user?: MemberInvitationUser | null;
}

const InviteRegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const routeState = (location.state as InviteRegisterRouteState | null) || null;
  const routeEmail = routeState?.email || "";

  const [isLoading, setIsLoading] = React.useState(false);
  const [isResolvingInvite, setIsResolvingInvite] = React.useState(false);
  const [result, setResult] = React.useState<MemberInvitationConfirmResponse | null>(null);
  const [resolvedEmail, setResolvedEmail] = React.useState(routeEmail);
  const [resolvedUser, setResolvedUser] = React.useState<MemberInvitationUser | null>(
    routeState?.user || null
  );

  const isPasswordSetupMode = Boolean(resolvedUser);
  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        phoneNumber: isPasswordSetupMode
          ? Yup.string()
          : Yup.string()
              .required("휴대폰 번호가 필요합니다.")
              .matches(/^[0-9]{10,11}$/, "휴대폰 번호는 숫자 10~11자리여야 합니다."),
        username: isPasswordSetupMode
          ? Yup.string()
          : Yup.string().required("이름이 필요합니다.").max(50, "최대 50자까지 가능합니다."),
        password: Yup.string()
          .min(4, "비밀번호는 최소 4자리 이상이어야 합니다.")
          .required("비밀번호를 입력해주세요."),
        passwordConfirm: Yup.string()
          .oneOf([Yup.ref("password"), null as any], "비밀번호가 일치하지 않습니다.")
          .required("비밀번호 확인이 필요합니다."),
      }),
    [isPasswordSetupMode]
  );

  React.useEffect(() => {
    if (!token || routeEmail) return;

    setIsResolvingInvite(true);
    membershipService.confirmInvitation({ token })
      .then((response) => {
        const data = response.data;
        if (data.requires_registration) {
          setResolvedEmail(data.email || "");
          setResolvedUser(data.user || null);
          return;
        }
        setResult(data);
      })
      .catch((error) => {
        console.log("invite register preflight error", error);
      })
      .finally(() => {
        setIsResolvingInvite(false);
      });
  }, [token, routeEmail]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: "",
      username: "",
      password: "",
      passwordConfirm: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) {
        toast.error("초대 토큰이 없습니다.");
        return;
      }

      setIsLoading(true);
      try {
        const payload = {
          token,
          password: values.password,
          ...(isPasswordSetupMode
            ? (resolvedUser?.phone_num ? { phone_number: resolvedUser.phone_num } : {})
            : {
                phone_number: values.phoneNumber,
                username: values.username,
              }),
        };

        const response = await membershipService.confirmInvitation(payload);
        setResult(response.data);
        toast.success(response.data.message);
      } catch (error) {
        console.log("invite register error", error);
        const apiData = error?.response?.data;
        toast.error(apiData?.message || apiData?.detail || "가입 처리에 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!token) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={6}>
              <Card>
                <CardHeader className="border-0">
                  <h5 className="card-title mb-0">초대 토큰 필요</h5>
                </CardHeader>
                <div className="card-body">
                  <p className="mb-3">초대 링크가 유효하지 않습니다.</p>
                  <Button color="secondary" onClick={() => navigate("/login")}>
                    로그인 화면으로 이동
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={6}>
            <Card>
              <CardHeader className="border-0">
                <h5 className="card-title mb-0">
                  {isPasswordSetupMode ? "관리자 최초 비밀번호 설정" : "멤버 초대 계정 등록"}
                </h5>
              </CardHeader>
              <div className="card-body">
                {!result ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      formik.handleSubmit();
                      return false;
                    }}
                  >
                    <p className="text-muted mb-4">
                      초대된 이메일: {isResolvingInvite ? "확인 중..." : (resolvedEmail || "초대 링크에서 확인")}
                    </p>

                    {isPasswordSetupMode ? (
                      <div className="rounded border border-dashed p-3 mb-4">
                        <p className="mb-1">이름: {resolvedUser?.username || "-"}</p>
                        <p className="mb-1">휴대폰 번호: {resolvedUser?.phone_num || "-"}</p>
                        <p className="mb-0">이메일: {resolvedUser?.email || resolvedEmail || "-"}</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <label htmlFor="phoneNumber" className="form-label">
                            휴대폰 번호
                          </label>
                          <input
                            type="text"
                            id="phoneNumber"
                            name="phoneNumber"
                            className="form-control"
                            placeholder="01012345678"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.phoneNumber}
                          />
                          {(formik.touched.phoneNumber && formik.errors.phoneNumber) ? (
                            <div className="invalid-feedback d-block">{formik.errors.phoneNumber}</div>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="username" className="form-label">
                            이름
                          </label>
                          <input
                            type="text"
                            id="username"
                            name="username"
                            className="form-control"
                            placeholder="홍길동"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.username}
                          />
                          {(formik.touched.username && formik.errors.username) ? (
                            <div className="invalid-feedback d-block">{formik.errors.username}</div>
                          ) : null}
                        </div>
                      </>
                    )}

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        비밀번호
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        className="form-control"
                        placeholder="비밀번호"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                      />
                      {(formik.touched.password && formik.errors.password) ? (
                        <div className="invalid-feedback d-block">{formik.errors.password}</div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="passwordConfirm" className="form-label">
                        비밀번호 확인
                      </label>
                      <input
                        type="password"
                        id="passwordConfirm"
                        name="passwordConfirm"
                        className="form-control"
                        placeholder="비밀번호 확인"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.passwordConfirm}
                      />
                      {(formik.touched.passwordConfirm && formik.errors.passwordConfirm) ? (
                        <div className="invalid-feedback d-block">{formik.errors.passwordConfirm}</div>
                      ) : null}
                    </div>

                    <div className="text-end">
                      <Button type="submit" color="success" disabled={isLoading || isResolvingInvite}>
                        {isLoading
                          ? "처리 중..."
                          : (isPasswordSetupMode ? "비밀번호 설정 후 초대 승인" : "회원가입 후 초대 승인")}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <p className="mb-3">{result.message}</p>
                    {result.user ? (
                      <p className="mb-3">
                        계정: {result.user.username} ({result.user.phone_num})
                      </p>
                    ) : null}
                    <Button
                      color="secondary"
                      onClick={() => navigate("/login")}
                    >
                      로그인 화면으로 이동
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
      <Toaster position="top-center" />
    </div>
  );
};

export default InviteRegisterPage;
